import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateMetaTags, DEFAULT_META } from '../utils/seo';
import { generateOrganizationSchema, generateWebSiteSchema, insertMultipleStructuredData } from '../utils/structuredData';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Alert,
  useTheme,
  alpha,
  Skeleton,
  Paper,
  Divider,
  useMediaQuery,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import MainLayout, { useHeaderVisibility } from '../components/layout/MainLayout';
import BannerCarousel from '../components/BannerCarousel';
import { ApiService } from '../services/api';
import { Restaurant, Category, Banner } from '../types';
import {
  StarFilledIcon,
  LocationIcon,
  ReviewIcon,
  EyeIcon,
  HeartFilledIcon,
  NewIcon,
} from '../components/icons/CustomIcons';
import { AccessTime, Visibility, RateReview } from '@mui/icons-material';
import { DEFAULT_RESTAURANT_IMAGE, handleImageError } from '../constants/images';
import { useLanguage } from '../context/LanguageContext';
import { useScrollDirection } from '../hooks/useScrollDirection';

// 정렬 옵션 타입
type SortOption = 'rating_desc' | 'review_count_desc' | 'view_count_desc' | 'created_at_desc';

interface PushedRestaurant {
  id: number;
  title: string;
  subtitle?: string;
  description?: string;
  badge_text?: string;
  badge_color?: string;
  restaurant: Restaurant;
}

const NewHomePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useLanguage();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // 스크롤 방향 감지 (카테고리바 표시/숨김용)
  const { isVisible: isHeaderVisible } = useScrollDirection({ threshold: 10, alwaysShowAtTop: true });
  // 헤더 높이는 Context에서 가져옴 (MainLayout에서 측정)
  const { headerHeight } = useHeaderVisibility();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [pushedRestaurants, setPushedRestaurants] = useState<PushedRestaurant[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showCategorySidebar, setShowCategorySidebar] = useState(true);

  // 다양한 알고리즘별 맛집 상태 (PC용)
  const [ratingRestaurants, setRatingRestaurants] = useState<Restaurant[]>([]);
  const [reviewCountRestaurants, setReviewCountRestaurants] = useState<Restaurant[]>([]);
  const [viewCountRestaurants, setViewCountRestaurants] = useState<Restaurant[]>([]);
  const [favoriteRestaurants, setFavoriteRestaurants] = useState<Restaurant[]>([]);
  const [latestRestaurants, setLatestRestaurants] = useState<Restaurant[]>([]);

  // PC용 섹션별 확장 상태 및 페이지 관리
  const [sectionPages, setSectionPages] = useState<{ [key: string]: number }>({
    rating_desc: 1,
    review_count_desc: 1,
    view_count_desc: 1,
    favorite_count_desc: 1,
    created_at_desc: 1,
  });
  const [sectionLoading, setSectionLoading] = useState<{ [key: string]: boolean }>({});
  const [sectionHasMore, setSectionHasMore] = useState<{ [key: string]: boolean }>({
    rating_desc: true,
    review_count_desc: true,
    view_count_desc: true,
    favorite_count_desc: true,
    created_at_desc: true,
  });
  const SECTION_PAGE_SIZE = 8;

  // 모바일용 단일 목록 상태
  const [mobileRestaurants, setMobileRestaurants] = useState<Restaurant[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mobilePage, setMobilePage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sortOption, setSortOption] = useState<SortOption>('created_at_desc');
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const isLoadingRef = useRef(false); // 중복 요청 방지용
  const MOBILE_PAGE_SIZE = 10;

  // 정렬 옵션 목록 (아이콘 포함)
  const sortOptions: { value: SortOption; label: string; icon: React.ReactNode }[] = [
    { value: 'created_at_desc', label: '최신순', icon: <AccessTime sx={{ fontSize: 18 }} /> },
    { value: 'rating_desc', label: '별점순', icon: <StarFilledIcon sx={{ fontSize: 18 }} /> },
    { value: 'review_count_desc', label: '리뷰순', icon: <RateReview sx={{ fontSize: 18 }} /> },
    { value: 'view_count_desc', label: '조회순', icon: <Visibility sx={{ fontSize: 18 }} /> },
  ];

  const loadRestaurantsByCategory = useCallback(async (categoryIds: string[]) => {
    try {
      // 통합 API로 한 번에 모든 정렬 방식의 맛집 로드
      const params = categoryIds.length > 0 ? { category_ids: categoryIds.join(','), limit: SECTION_PAGE_SIZE } : { limit: SECTION_PAGE_SIZE };

      const multiSortRes = await ApiService.getRestaurantsMultiSort(params);

      if (multiSortRes.success && multiSortRes.data) {
        setRatingRestaurants(multiSortRes.data.byRating || []);
        setReviewCountRestaurants(multiSortRes.data.byReviewCount || []);
        setViewCountRestaurants(multiSortRes.data.byViewCount || []);
        setFavoriteRestaurants(multiSortRes.data.byFavoriteCount || []);
        setLatestRestaurants(multiSortRes.data.byLatest || []);

        // 섹션 상태 리셋
        setSectionPages({
          rating_desc: 1,
          review_count_desc: 1,
          view_count_desc: 1,
          favorite_count_desc: 1,
          created_at_desc: 1,
        });
        setSectionHasMore({
          rating_desc: (multiSortRes.data.byRating || []).length >= SECTION_PAGE_SIZE,
          review_count_desc: (multiSortRes.data.byReviewCount || []).length >= SECTION_PAGE_SIZE,
          view_count_desc: (multiSortRes.data.byViewCount || []).length >= SECTION_PAGE_SIZE,
          favorite_count_desc: (multiSortRes.data.byFavoriteCount || []).length >= SECTION_PAGE_SIZE,
          created_at_desc: (multiSortRes.data.byLatest || []).length >= SECTION_PAGE_SIZE,
        });
      }
    } catch (err: any) {
      console.error('Failed to load restaurants:', err);
    }
  }, [SECTION_PAGE_SIZE]);

  // PC용 섹션별 더보기 로드 함수
  const loadMoreForSection = useCallback(async (sortParam: string) => {
    if (sectionLoading[sortParam] || !sectionHasMore[sortParam]) return;

    setSectionLoading(prev => ({ ...prev, [sortParam]: true }));

    try {
      const nextPage = sectionPages[sortParam] + 1;
      const params: any = {
        page: nextPage,
        limit: SECTION_PAGE_SIZE,
        sort: sortParam,
      };
      if (selectedCategoryIds.length > 0) {
        params.category_ids = selectedCategoryIds.join(',');
      }

      const res = await ApiService.getRestaurants(params);

      if (res.success && res.data) {
        const newRestaurants = res.data.restaurants || [];

        // 해당 섹션에 데이터 추가
        if (sortParam === 'rating_desc') {
          setRatingRestaurants(prev => [...prev, ...newRestaurants]);
        } else if (sortParam === 'review_count_desc') {
          setReviewCountRestaurants(prev => [...prev, ...newRestaurants]);
        } else if (sortParam === 'view_count_desc') {
          setViewCountRestaurants(prev => [...prev, ...newRestaurants]);
        } else if (sortParam === 'favorite_count_desc') {
          setFavoriteRestaurants(prev => [...prev, ...newRestaurants]);
        } else if (sortParam === 'created_at_desc') {
          setLatestRestaurants(prev => [...prev, ...newRestaurants]);
        }

        setSectionPages(prev => ({ ...prev, [sortParam]: nextPage }));

        // 더 이상 데이터가 없으면 hasMore를 false로
        if (newRestaurants.length < SECTION_PAGE_SIZE) {
          setSectionHasMore(prev => ({ ...prev, [sortParam]: false }));
        }
      }
    } catch (err: any) {
      console.error('Failed to load more restaurants:', err);
    } finally {
      setSectionLoading(prev => ({ ...prev, [sortParam]: false }));
    }
  }, [sectionLoading, sectionHasMore, sectionPages, selectedCategoryIds, SECTION_PAGE_SIZE]);

  // 모바일용 맛집 로드 함수
  const loadMobileRestaurants = useCallback(async (categoryIds: string[], page: number, sort: SortOption, reset: boolean = false) => {
    // 중복 요청 방지
    if (isLoadingRef.current && !reset) return;

    isLoadingRef.current = true;
    setLoadingMore(true);

    try {
      const params: any = {
        page,
        limit: MOBILE_PAGE_SIZE,
        sort,
      };
      if (categoryIds.length > 0) {
        params.category_ids = categoryIds.join(',');
      }

      const res = await ApiService.getRestaurants(params);

      if (res.success && res.data) {
        const newRestaurants = res.data.restaurants || [];

        if (reset) {
          setMobileRestaurants(newRestaurants);
          setMobilePage(1);
        } else {
          setMobileRestaurants(prev => [...prev, ...newRestaurants]);
        }

        // 더 이상 데이터가 없으면 hasMore를 false로
        if (newRestaurants.length < MOBILE_PAGE_SIZE) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (err: any) {
      console.error('Failed to load mobile restaurants:', err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
      isLoadingRef.current = false;
    }
  }, []);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      // 모든 데이터를 한 번에 로드 (최적화)
      const homeDataRes = await ApiService.getHomeData();

      if (homeDataRes.success && homeDataRes.data) {
        // 배너, 푸시 맛집 설정
        setBanners(homeDataRes.data.banners || []);
        setPushedRestaurants(homeDataRes.data.pushedRestaurants || []);

        // 카테고리 설정 (별도 API 호출 제거)
        setCategories(homeDataRes.data.categories || []);

        // Multi-sort 맛집 데이터 설정 (별도 API 호출 제거)
        if (homeDataRes.data.multiSort) {
          setRatingRestaurants(homeDataRes.data.multiSort.byRating || []);
          setReviewCountRestaurants(homeDataRes.data.multiSort.byReviewCount || []);
          setViewCountRestaurants(homeDataRes.data.multiSort.byViewCount || []);
          setFavoriteRestaurants(homeDataRes.data.multiSort.byFavoriteCount || []);
          setLatestRestaurants(homeDataRes.data.multiSort.byLatest || []);

          // 모바일용 초기 데이터 (byLatest 사용 - 최신순이 기본)
          setMobileRestaurants(homeDataRes.data.multiSort.byLatest || []);
          setHasMore((homeDataRes.data.multiSort.byLatest || []).length >= MOBILE_PAGE_SIZE);
        }
      }
    } catch (err: any) {
      setError(err.userMessage || '데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // SEO: 메타 태그 및 구조화된 데이터 설정
  useEffect(() => {
    updateMetaTags({
      ...DEFAULT_META,
      url: window.location.href
    });

    // 구조화된 데이터 추가 (Organization, WebSite)
    const schemas = [
      generateOrganizationSchema(),
      generateWebSiteSchema()
    ];
    insertMultipleStructuredData(schemas);
  }, []);

  useEffect(() => {
    // 초기 로드가 완료되고, 카테고리가 변경되었을 때 실행
    if (!isInitialLoad) {
      loadRestaurantsByCategory(selectedCategoryIds);
      // 모바일: 카테고리 변경 시 목록 리셋
      if (isMobile) {
        setMobilePage(1);
        setHasMore(true);
        loadMobileRestaurants(selectedCategoryIds, 1, sortOption, true);
      }
    }
  }, [selectedCategoryIds, isInitialLoad, loadRestaurantsByCategory, isMobile, loadMobileRestaurants, sortOption]);

  // 모바일 무한 스크롤: IntersectionObserver
  useEffect(() => {
    if (!isMobile) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !isLoadingRef.current) {
          setMobilePage(prev => {
            const nextPage = prev + 1;
            loadMobileRestaurants(selectedCategoryIds, nextPage, sortOption, false);
            return nextPage;
          });
        }
      },
      { threshold: 0.5, rootMargin: '100px' }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [isMobile, hasMore, selectedCategoryIds, sortOption, loadMobileRestaurants]);

  // 정렬 변경 핸들러
  const handleSortChange = (newSort: SortOption) => {
    if (newSort !== sortOption) {
      setSortOption(newSort);
      setMobilePage(1);
      setHasMore(true);
      setMobileRestaurants([]);
      loadMobileRestaurants(selectedCategoryIds, 1, newSort, true);
    }
  };

  // 카테고리 클릭 핸들러 (토글 방식 - 중복 선택 가능)
  const handleCategoryClick = (categoryId: string | null) => {
    if (categoryId === null) {
      // "전체" 클릭 시 모든 선택 해제
      setSelectedCategoryIds([]);
    } else {
      setSelectedCategoryIds(prev => {
        if (prev.includes(categoryId)) {
          // 이미 선택된 카테고리면 제거
          return prev.filter(id => id !== categoryId);
        } else {
          // 선택되지 않은 카테고리면 추가
          return [...prev, categoryId];
        }
      });
    }
  };

  const handleRestaurantClick = (restaurantId: string) => {
    navigate(`/restaurants/${restaurantId}`);
  };

  const renderRating = (rating: number) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <StarFilledIcon sx={{ fontSize: 18, color: '#FFD93D' }} />
        <Typography variant="body2" fontWeight={600}>
          {rating.toFixed(1)}
        </Typography>
      </Box>
    );
  };

  const RestaurantCard: React.FC<{ restaurant: Restaurant }> = ({ restaurant }) => {
    const images = restaurant.images || [];
    const hasMultipleImages = images.length >= 3;

    // 모바일: 멀티 이미지 레이아웃 (메인 + 서브 2개)
    if (isMobile) {
      return (
        <Box
          sx={{
            cursor: 'pointer',
            '&:active': {
              opacity: 0.7,
            },
          }}
          onClick={() => handleRestaurantClick(restaurant.id)}
        >
          {/* 이미지 영역 - 멀티 이미지 레이아웃 */}
          {hasMultipleImages ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '70% 1fr',
                gridTemplateRows: '1fr 1fr',
                gap: '4px',
                borderRadius: 2,
                overflow: 'hidden',
                mb: 1,
                aspectRatio: '16/9',
              }}
            >
              {/* 메인 이미지 (왼쪽 - 2행 차지) */}
              <Box
                sx={{
                  gridRow: '1 / 3',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={images[0] || DEFAULT_RESTAURANT_IMAGE}
                  alt={restaurant.name}
                  onError={handleImageError}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                {/* 카테고리 뱃지 */}
                {restaurant.categories && (
                  <Chip
                    label={restaurant.categories.name}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      color: '#fff',
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      height: 22,
                    }}
                  />
                )}
                {/* 평점 배지 */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 10,
                    left: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.4,
                    px: 1,
                    py: 0.4,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    borderRadius: 1,
                  }}
                >
                  <StarFilledIcon sx={{ fontSize: 14, color: '#FFD93D' }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#fff' }}>
                    {restaurant.rating.toFixed(1)}
                  </Typography>
                </Box>
              </Box>

              {/* 서브 이미지 1 (오른쪽 상단) */}
              <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                <Box
                  component="img"
                  src={images[1] || DEFAULT_RESTAURANT_IMAGE}
                  alt={`${restaurant.name} 2`}
                  onError={handleImageError}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>

              {/* 서브 이미지 2 (오른쪽 하단) */}
              <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                <Box
                  component="img"
                  src={images[2] || DEFAULT_RESTAURANT_IMAGE}
                  alt={`${restaurant.name} 3`}
                  onError={handleImageError}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                mb: 1,
                aspectRatio: '4/3',
              }}
            >
              <Box
                component="img"
                src={images[0] || DEFAULT_RESTAURANT_IMAGE}
                alt={restaurant.name}
                onError={handleImageError}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              {restaurant.categories && (
                <Chip
                  label={restaurant.categories.name}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    color: '#fff',
                    fontWeight: 500,
                    fontSize: '0.7rem',
                    height: 22,
                  }}
                />
              )}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 10,
                  left: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.4,
                  px: 1,
                  py: 0.4,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: 1,
                }}
              >
                <StarFilledIcon sx={{ fontSize: 14, color: '#FFD93D' }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#fff' }}>
                  {restaurant.rating.toFixed(1)}
                </Typography>
              </Box>
            </Box>
          )}

          {/* 텍스트 영역 */}
          <Box sx={{ px: 0.5 }}>
            {/* 가게 이름 */}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.95rem',
                lineHeight: 1.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mb: 0.3,
              }}
            >
              {restaurant.name}
            </Typography>

            {/* 주소 */}
            <Typography
              sx={{
                fontSize: '0.72rem',
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mb: 0.3,
              }}
            >
              {restaurant.address}
            </Typography>

            {/* 리뷰 수 · 조회수 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
                리뷰 {restaurant.review_count || 0}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
                ·
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
                조회 {restaurant.view_count || 0}
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    // PC: 기존 오버레이 스타일
    return (
      <Card
        sx={{
          position: 'relative',
          height: { sm: 200, md: 220 },
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'all 0.25s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            '& .restaurant-image': {
              transform: 'scale(1.05)',
            },
            '& .restaurant-overlay': {
              background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.15) 100%)',
            },
          },
          '&:active': {
            transform: 'translateY(-2px)',
          },
        }}
        onClick={() => handleRestaurantClick(restaurant.id)}
      >
        <CardMedia
          component="img"
          className="restaurant-image"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.3s ease',
          }}
          image={restaurant.images?.[0] || DEFAULT_RESTAURANT_IMAGE}
          alt={restaurant.name}
          onError={handleImageError}
        />

        <Box
          className="restaurant-overlay"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 100%)',
            transition: 'background 0.3s ease',
          }}
        />

        <CardContent
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            color: 'white',
            p: 2.5,
            background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ flex: 1, color: 'white', fontSize: '1.25rem' }}>
              {restaurant.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
              <StarFilledIcon sx={{ fontSize: 18, color: '#FFD93D' }} />
              <Typography variant="body2" fontWeight={600} sx={{ color: 'white' }}>
                {restaurant.rating.toFixed(1)}
              </Typography>
            </Box>
          </Box>

          {restaurant.categories && (
            <Chip
              label={restaurant.categories.name}
              size="small"
              sx={{
                mb: 1,
                backgroundColor: alpha(theme.palette.primary.main, 0.9),
                color: '#FFFFFF',
                fontWeight: 600,
                border: 'none',
                height: 24,
                fontSize: '0.8rem',
              }}
            />
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <LocationIcon sx={{ fontSize: 16, color: 'white' }} />
            <Typography variant="caption" noWrap sx={{ fontSize: '0.875rem', color: 'white' }}>
              {restaurant.address}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.9)' }}>
              {t('restaurant.reviewCount')} {restaurant.review_count || 0}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.9)' }}>
              {t('restaurant.viewCount')} {restaurant.view_count || 0}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const RestaurantSection: React.FC<{
    title: string;
    icon: React.ReactNode;
    restaurants: Restaurant[];
    sortParam: string;
  }> = ({ title, icon, restaurants, sortParam }) => {
    if (restaurants.length === 0) return null;

    const isLoading = sectionLoading[sortParam];
    const hasMore = sectionHasMore[sortParam];

    return (
      <Box sx={{
        mb: { xs: 6, md: 8 },
      }}>
        {/* 섹션 헤더 */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: { xs: 2.5, md: 3 },
        }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main',
              '& svg': {
                fontSize: { xs: 22, md: 26 },
              },
            }}
          >
            {icon}
          </Box>
          <Typography variant="h4" fontWeight={600} sx={{ fontSize: { xs: '1.2rem', sm: '1.35rem', md: '1.5rem' }, letterSpacing: '-0.02em' }}>
            {title}
          </Typography>
        </Box>

        {/* 리스트 레이아웃 */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: { xs: 2, md: 3 },
          }}
        >
          {restaurants.map((restaurant) => (
            <RestaurantCard key={restaurant.id} restaurant={restaurant} />
          ))}
        </Box>

        {/* 더보기 버튼 - 섹션 하단 중앙 */}
        {hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => loadMoreForSection(sortParam)}
              disabled={isLoading}
              sx={{
                px: 4,
                py: 1.2,
                borderRadius: 2,
                fontSize: '0.9rem',
                fontWeight: 500,
                textTransform: 'none',
                borderColor: 'divider',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={20} sx={{ mr: 1 }} />
              ) : null}
              {isLoading ? '불러오는 중...' : `${title} 더보기`}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <MainLayout>
        <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, md: 3 } }}>
          <Skeleton variant="rectangular" sx={{ height: { xs: 200, md: 400 }, borderRadius: 2, mb: { xs: 2, md: 4 } }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: { xs: 2, md: 3 } }}>
            {[1, 2, 3, 4].map((i) => (
              <Box key={i}>
                <Skeleton variant="rectangular" sx={{ height: { xs: 200, md: 300 }, borderRadius: 2 }} />
              </Box>
            ))}
          </Box>
        </Container>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <Container maxWidth="xl" sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, md: 3 } }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </MainLayout>
    );
  }

  const selectedCategories = categories.filter((c) => selectedCategoryIds.includes(c.id));

  return (
    <MainLayout>
      {/* 배너 캐러셀 - 상단 여백 추가, PC 폭 제한 */}
      {banners.length > 0 && (
        <Container
          maxWidth="lg"
          sx={{
            mt: { xs: 2, md: 3 },
            mb: { xs: 4, md: 8 },
            px: { xs: 0, md: 3 },
          }}
        >
          <BannerCarousel banners={banners} />
        </Container>
      )}

      {/* 푸시 맛집 섹션 - 두 번째 배치 */}
      {pushedRestaurants.length > 0 && (
        <Box
          sx={{
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.02)'
              : 'rgba(0,0,0,0.02)',
            py: { xs: 5, md: 8 },
            mb: { xs: 4, md: 8 },
          }}
        >
          <Container maxWidth="xl">
            <Typography
              variant="h3"
              fontWeight={700}
              align="center"
              gutterBottom
              sx={{
                color: 'text.primary',
                mb: { xs: 4, md: 6 },
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.25rem' },
                letterSpacing: '-0.02em',
              }}
            >
              {t('home.featuredTitle')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: { xs: 2, md: 4 },
              }}
            >
              {pushedRestaurants.map((pushed, index) => (
                    <Card
                      key={pushed.id}
                      sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      '& .pushed-image': {
                        transform: 'scale(1.03)',
                      },
                    },
                    '&:active': {
                      transform: 'translateY(-2px)',
                    },
                  }}
                  onClick={() => handleRestaurantClick(pushed.restaurant.id)}
                >
                  {/* 배지 */}
                  {pushed.badge_text && (
                    <Chip
                      label={pushed.badge_text}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 2,
                        backgroundColor: pushed.badge_color || theme.palette.primary.main,
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 26,
                      }}
                    />
                  )}

                  <CardMedia
                    component="img"
                    className="pushed-image"
                    sx={{
                      height: { xs: 160, sm: 180, md: 220 },
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease',
                    }}
                    image={pushed.restaurant.images?.[0] || DEFAULT_RESTAURANT_IMAGE}
                    alt={pushed.restaurant.name}
                    onError={handleImageError}
                  />

                  <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                    <Typography variant="overline" color="primary" fontWeight={700} sx={{ display: 'block', mb: { xs: 0.5, md: 1 }, fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                      {pushed.title}
                    </Typography>
                    <Typography variant="h5" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '0.95rem', md: '1.5rem' }, mb: { xs: 0.5, md: 1 } }}>
                      {pushed.restaurant.name}
                    </Typography>
                    {pushed.subtitle && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 1, md: 2 }, fontSize: { xs: '0.75rem', md: '0.875rem' }, lineHeight: 1.3 }}>
                        {pushed.subtitle}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: { xs: 1, md: 2 } }}>
                      {renderRating(pushed.restaurant.rating)}
                      <Box sx={{ display: 'flex', gap: { xs: 0.5, md: 2 }, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                          {t('restaurant.reviewCount')} {pushed.restaurant.review_count || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                          {t('restaurant.viewCount')} {pushed.restaurant.view_count || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Container>
        </Box>
      )}

      {/* 메인 콘텐츠 */}
      {isMobile ? (
        /* ===== 모바일 레이아웃 ===== */
        <>
          {/* Sticky 카테고리 + 정렬 필터 바 - 푸시 섹션 아래에 배치 */}
          <Box
            sx={{
              position: 'sticky',
              top: isHeaderVisible ? headerHeight : 0,
              zIndex: 99,
              backgroundColor: theme.palette.background.paper,
              borderBottom: `1px solid ${theme.palette.divider}`,
              py: 1.5,
              px: 2,
              transition: 'top 0.3s ease',
            }}
          >
            {/* 카테고리 행 */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                pb: 1.5,
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
              }}
            >
              <Chip
                label="전체"
                size="small"
                onClick={() => handleCategoryClick(null)}
                sx={{
                  flexShrink: 0,
                  backgroundColor: selectedCategoryIds.length === 0 ? theme.palette.primary.main : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                  color: selectedCategoryIds.length === 0 ? 'white' : 'text.primary',
                  border: 'none',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  height: 32,
                  '&:hover': {
                    backgroundColor: selectedCategoryIds.length === 0 ? theme.palette.primary.dark : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                  },
                }}
              />
              {categories.slice(0, 10).map((category) => (
                <Chip
                  key={category.id}
                  label={category.name}
                  size="small"
                  onClick={() => handleCategoryClick(category.id)}
                  sx={{
                    flexShrink: 0,
                    backgroundColor: selectedCategoryIds.includes(category.id) ? theme.palette.primary.main : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                    color: selectedCategoryIds.includes(category.id) ? 'white' : 'text.primary',
                    border: 'none',
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    height: 32,
                    '&:hover': {
                      backgroundColor: selectedCategoryIds.includes(category.id) ? theme.palette.primary.dark : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                    },
                  }}
                />
              ))}
            </Box>
            {/* 정렬 행 - 아이콘 기반 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {sortOptions.map((opt) => {
                const isSelected = sortOption === opt.value;
                return (
                  <Box
                    key={opt.value}
                    onClick={() => handleSortChange(opt.value)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      cursor: 'pointer',
                      px: isSelected ? 1.5 : 1,
                      py: 0.5,
                      borderRadius: 2,
                      backgroundColor: isSelected
                        ? theme.palette.primary.main
                        : theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.06)'
                          : 'rgba(0,0,0,0.04)',
                      color: isSelected ? 'white' : 'text.secondary',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: isSelected
                          ? theme.palette.primary.dark
                          : theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.1)'
                            : 'rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    {opt.icon}
                    {isSelected && (
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {opt.label}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* 단일 맛집 목록 - 1열 */}
          <Container maxWidth="xl" sx={{ px: 2, py: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {mobileRestaurants.map((restaurant) => (
                <RestaurantCard key={restaurant.id} restaurant={restaurant} />
              ))}
            </Box>

            {/* 무한 스크롤 트리거 */}
            {hasMore && (
              <Box ref={loadMoreRef} sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                {loadingMore && <CircularProgress size={24} />}
              </Box>
            )}

            {!hasMore && mobileRestaurants.length > 0 && (
              <Typography sx={{ textAlign: 'center', py: 3, color: 'text.disabled', fontSize: '0.8rem' }}>
                모든 맛집을 불러왔습니다
              </Typography>
            )}
          </Container>
        </>
      ) : (
        /* ===== PC 레이아웃 ===== */
        <Container maxWidth="xl" sx={{ px: 3 }}>
          <Box sx={{ display: 'flex', gap: 4, position: 'relative' }}>
            {/* 메인 콘텐츠 영역 */}
            <Box sx={{ flex: 1, minWidth: 0, transition: 'all 0.3s ease' }}>
              {/* 선택된 카테고리 표시 */}
              {selectedCategoryIds.length > 0 && selectedCategories.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {selectedCategories.map((category) => (
                      <Chip
                        key={category.id}
                        label={category.name}
                        onDelete={() => handleCategoryClick(category.id)}
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '1rem',
                          py: 2.5,
                          '& .MuiChip-deleteIcon': {
                            color: 'white',
                          },
                        }}
                      />
                    ))}
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', ml: 1 }}>
                      {t('home.selectedCategory')}
                    </Typography>
                  </Box>
                  <Divider />
                </Box>
              )}

              {/* 다양한 알고리즘별 맛집 섹션 */}
              <RestaurantSection
                title={t.home.highRatedRestaurants}
                icon={<StarFilledIcon />}
                restaurants={ratingRestaurants}
                sortParam="rating_desc"
              />
              <RestaurantSection
                title={t('home.mostReviewedRestaurants')}
                icon={<ReviewIcon />}
                restaurants={reviewCountRestaurants}
                sortParam="review_count_desc"
              />
              <RestaurantSection
                title={t('home.mostViewedRestaurants')}
                icon={<EyeIcon />}
                restaurants={viewCountRestaurants}
                sortParam="view_count_desc"
              />
              <RestaurantSection
                title={t('home.mostLikedRestaurants')}
                icon={<HeartFilledIcon />}
                restaurants={favoriteRestaurants}
                sortParam="favorite_count_desc"
              />
              <RestaurantSection
                title={t('home.newRestaurants')}
                icon={<NewIcon />}
                restaurants={latestRestaurants}
                sortParam="created_at_desc"
              />
            </Box>

          {/* 우측 카테고리 사이드바 영역 (Sticky) */}
          <Box
            sx={{
              width: showCategorySidebar ? 250 : 60,
              display: { xs: 'none', lg: 'flex' },
              flexDirection: 'column',
              alignItems: showCategorySidebar ? 'stretch' : 'center',
              transition: 'all 0.3s ease',
            }}
          >
            {/* 토글 버튼 - Sticky */}
            <Tooltip title={showCategorySidebar ? '카테고리 숨기기' : '카테고리 보기'} placement="left">
              <IconButton
                onClick={() => setShowCategorySidebar(!showCategorySidebar)}
                sx={{
                  position: 'sticky',
                  top: 100,
                  alignSelf: showCategorySidebar ? 'flex-start' : 'center',
                  mb: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  },
                  zIndex: 10,
                }}
              >
                {showCategorySidebar ? <ChevronRight /> : <ChevronLeft />}
              </IconButton>
            </Tooltip>

            {/* 카테고리 사이드바 Paper */}
            {showCategorySidebar && (
            <Paper
              elevation={0}
              sx={{
                position: 'sticky',
                top: 140,
                p: 3,
                borderRadius: 3,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2.5, color: 'text.primary' }}>
                {t.home.categories}
              </Typography>

              {/* 전체 보기 버튼 */}
              <Button
                fullWidth
                variant={selectedCategoryIds.length === 0 ? 'contained' : 'text'}
                onClick={() => handleCategoryClick(null)}
                sx={{
                  mb: 2,
                  justifyContent: 'flex-start',
                  py: 1.2,
                  px: 2,
                  fontWeight: selectedCategoryIds.length === 0 ? 600 : 500,
                  backgroundColor: selectedCategoryIds.length === 0 ? 'primary.main' : 'transparent',
                  color: selectedCategoryIds.length === 0 ? 'white' : 'text.primary',
                  '&:hover': {
                    backgroundColor: selectedCategoryIds.length === 0 ? 'primary.dark' : alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                {t('home.viewAll')}
              </Button>

              <Divider sx={{ my: 2 }} />

              {/* 카테고리 버튼들 - 2열 grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    variant="text"
                    onClick={() => handleCategoryClick(category.id)}
                    sx={{
                      py: 1,
                      px: 1.5,
                      fontSize: '0.85rem',
                      fontWeight: selectedCategoryIds.includes(category.id) ? 600 : 400,
                      color: selectedCategoryIds.includes(category.id) ? 'primary.main' : 'text.secondary',
                      backgroundColor: selectedCategoryIds.includes(category.id) ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                      justifyContent: 'flex-start',
                      minHeight: '40px',
                      borderRadius: 2,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    <Typography variant="body2" fontWeight="inherit" noWrap>
                      {category.name}
                    </Typography>
                  </Button>
                ))}
              </Box>
            </Paper>
            )}
          </Box>
        </Box>
      </Container>
      )}
    </MainLayout>
  );
};

export default NewHomePage;
